/**
 * Short diagnostic lines for tracking down slow loads and freezes. They show
 * in the browser console and, in the iOS app, in Xcode's console as
 * "[NN] +12.3s event {...}". Filter the console by "[NN]" to see only these.
 */

const startedAt = Date.now();
let lastEvent = "start";

// The last lines are also kept on the device (Postavke → Dijagnostika) so they
// can be copied after a problem, even if the app was restarted since.
const LOG_KEY = "nn-diag-log";
const MAX_LINES = 400;
const lines: string[] = loadSavedLines();
let saveTimer: ReturnType<typeof setTimeout> | null = null;

function loadSavedLines(): string[] {
  try {
    const saved = JSON.parse(localStorage.getItem(LOG_KEY) || "[]");
    return Array.isArray(saved) ? saved.slice(-MAX_LINES) : [];
  } catch {
    return [];
  }
}

function saveLines() {
  saveTimer = null;
  try {
    localStorage.setItem(LOG_KEY, JSON.stringify(lines));
  } catch {
    // Storage full: the console still has the lines.
  }
}

function remember(line: string) {
  lines.push(line);
  if (lines.length > MAX_LINES) lines.splice(0, lines.length - MAX_LINES);
  if (!saveTimer) saveTimer = setTimeout(saveLines, 1000);
}

function clock(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

export function diag(event: string, data?: Record<string, unknown>) {
  if (event !== "freeze") lastEvent = event;
  const seconds = ((Date.now() - startedAt) / 1000).toFixed(1);
  let details = "";
  try {
    details = data ? " " + JSON.stringify(data) : "";
  } catch {
    details = "";
  }
  console.log(`[NN] +${seconds}s ${event}${details}`);
  remember(`${clock()} +${seconds}s ${event}${details}`);
}

/** Errors are kept in the saved log too (shortened). */
export function diagError(message: string) {
  remember(`${clock()} ERROR ${message.slice(0, 300)}`);
}

/** The saved diagnostic lines, oldest first. */
export function getDiagLog(): string[] {
  return [...lines];
}

export function clearDiagLog() {
  lines.length = 0;
  saveLines();
}

// Mark where each app start begins in the saved log.
remember(`===== ${new Date().toISOString()} app start =====`);
if (typeof document !== "undefined") {
  // Save right away when the app goes to the background (it may be killed).
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") saveLines();
  });
}

/** Milliseconds since `start` (from performance.now()), rounded. */
export function since(start: number): number {
  return Math.round(performance.now() - start);
}

/**
 * Logs whenever the app froze (JavaScript blocked) for longer than
 * `thresholdMs`, with the last diagnostic event before it, so a lag spike can
 * be tied to what caused it.
 *
 * Checks a timer a few times a second rather than every frame, so it doesn't
 * keep the phone drawing frames (and using battery) while nothing happens.
 */
export function installJankMonitor(thresholdMs = 250) {
  if (typeof document === "undefined") return;
  const intervalMs = 200;
  let last = performance.now();
  setInterval(() => {
    const now = performance.now();
    const blocked = now - last - intervalMs;
    // Hidden pages have their timers slowed down; that's not a freeze.
    if (blocked > thresholdMs && document.visibilityState === "visible") {
      diag("freeze", { ms: Math.round(blocked), after: lastEvent });
    }
    last = now;
  }, intervalMs);
  document.addEventListener("visibilitychange", () => {
    last = performance.now();
    diag(document.visibilityState === "visible" ? "app visible" : "app hidden");
  });
}

// Timestamps of recent steps (no log line each), reported by slow tab slides.
const marks: { name: string; at: number }[] = [];
export function mark(name: string) {
  marks.push({ name, at: performance.now() });
  if (marks.length > 50) marks.shift();
}
/** Steps since `start` as "name@ms" (ms after `start`). */
export function marksSince(start: number): string[] {
  return marks.filter((m) => m.at >= start).map((m) => `${m.name}@${Math.round(m.at - start)}`);
}

/**
 * Marks how long each render of a component took (render + commit, with its
 * children), as "name:ms" steps for slow tab slides. Call at the top of the
 * component; `useLayoutEffect` is passed in to keep this file React-free.
 */
export function markRender(name: string, useLayoutEffect: (fn: () => void) => void) {
  const start = performance.now();
  useLayoutEffect(() => {
    const ms = Math.round(performance.now() - start);
    if (ms >= 5) mark(`${name}:${ms}ms`);
  });
}
